import { useState, useRef } from "react";
import { UserRole } from "@/types";
import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { userApi } from "@/services/userService";
import { Roles } from "@/enums/roles.enum";
import { GetProjectResponseDto } from "@/dto/get-project-response.dto";
import { RemoveMemberDialog } from "./RemoveMemberDialog";
import { DeleteProjectInviteDialog } from "./DeleteProjectInviteDialog";
import { ResendProjectInviteDialog } from "./ResendProjectInviteDialog";
import { projectApi, UnifiedProjectInvitation } from "@/services/projectService";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Skeleton } from "../ui/skeleton";
import { GetUserResponseDto } from "@/dto/get-user-response.dto";
import { AxiosError } from "axios";

interface ProjectMembersTabProps {
  project: GetProjectResponseDto;
}

const inviteMemberSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  role: z.enum([Roles.VENDOR, Roles.CLIENT, Roles.CONTRACTOR, Roles.MEMBER] as const, {
    required_error: "Please select a role",
  }),
});

type InviteMemberValues = z.infer<typeof inviteMemberSchema>;

const ProjectMembersTab = ({ project }: ProjectMembersTabProps) => {
  const { addMemberToProject } = useProjects();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [checkedUsersMap, setCheckedUsersMap] = useState<
    Record<
      string,
      { exists: boolean; user?: GetUserResponseDto; error?: string }
    >
  >({});
  const [checkedUser, setCheckedUser] = useState<null | {
    exists: boolean;
    user?: GetUserResponseDto;
    error?: string;
  }>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<InviteMemberValues>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: {
      email: "",
      role: Roles.CONTRACTOR,
    },
  });
  // Helper to validate email
  const validateEmail = (email: string) => {
    // Simple regex for email validation
    return /^\S+@\S+\.\S+$/.test(email);
  };

  // Update handleCheckUser to use the cache
  const handleCheckUser = async () => {
    const email = form.getValues("email");
    setEmailError(null);
    setCheckedUser(null);
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      setCheckedUser({
        exists: false,
        error: "Please enter a valid email address",
      });
      setCheckedUsersMap((prev) => ({
        ...prev,
        [email]: { exists: false, error: "Please enter a valid email address" },
      }));
      return;
    }
    // Check if user is already a member, owner, or admin
    const isAdmin = user?.role === Roles.ADMIN && user.email === email;
    const isOwner = project.createdBy.email === email;
    const isMember = project.members.some((m) => m.email === email);
    if (isAdmin || isOwner || isMember) {
      setCheckedUser({
        exists: true,
        user: undefined,
        error: "User is already a member of this project",
      });
      setCheckedUsersMap((prev) => ({
        ...prev,
        [email]: {
          exists: true,
          user: undefined,
          error: "User is already a member of this project",
        },
      }));
      setEmailError("User is already a member of this project");
      return;
    }
    // Check if user already has a pending invite (not expired)
    if (
      projectInvites &&
      projectInvites.some(
        (invite) =>
          invite.email === email &&
          !invite.acceptedAt &&
          !invite.declinedAt &&
          new Date(invite.expiresAt) > new Date()
      )
    ) {
      setCheckedUser({
        exists: true,
        user: undefined,
        error: "User already has a pending invitation to this project.",
      });
      setCheckedUsersMap((prev) => ({
        ...prev,
        [email]: {
          exists: true,
          user: undefined,
          error: "User already has a pending invitation to this project.",
        },
      }));
      setEmailError("User already has a pending invitation to this project.");
      return;
    }
    if (checkedUsersMap[email]) {
      setCheckedUser(checkedUsersMap[email]);
      setEmailError(checkedUsersMap[email].error || null);
      return;
    }
    setIsCheckingUser(true);
    try {
      const userRes = await userApi.getUser(email);
      if (userRes && userRes.user) {
        const result = { exists: true, user: userRes.user };
        setCheckedUser(result);
        setCheckedUsersMap((prev) => ({ ...prev, [email]: result }));
      } else {
        const result = { exists: false };
        setCheckedUser(result);
        setCheckedUsersMap((prev) => ({ ...prev, [email]: result }));
      }
    } catch (err) {
      const result = { exists: false };
      setCheckedUser(result);
      setCheckedUsersMap((prev) => ({ ...prev, [email]: result }));
    } finally {
      setIsCheckingUser(false);
    }
  };

  const { data: projectInvites, isFetching: isFetchingProjectInvites } =
    useQuery<UnifiedProjectInvitation[]>({
      queryKey: ["projects", project._id, "invitations"],
      queryFn: () => {
        return projectApi.getProjectInvitations(project._id);
      },
      retry: 0,
      enabled: !!project._id,
    });

  // Check if current user can add members
  const canAddMembers =
    user?.role === Roles.ADMIN ||
    user?.role === Roles.CLIENT ||
    project.createdBy._id === user._id;

  // Check if current user can remove members
  const canRemoveMember = (memberId: string) => {
    if (!user) return false;
    if (user.role === Roles.ADMIN || user.role === Roles.CLIENT) return true;
    if (user._id === memberId) return true; // Can always remove self (leave)
    if (user._id === project.createdBy._id) {
      // Owner can remove everyone except admins and vendors (but not themselves)
      if (memberId === user._id) return true; // Owner can leave
      const member =
        project.members.find((m) => m._id === memberId) ||
        (project.createdBy._id === memberId ? project.createdBy : null);
      return (
        !!member &&
        member._id !== user._id &&
        ![Roles.ADMIN, Roles.VENDOR].includes(member.role)
      );
    }
    return false;
  };

  // Check if current user can delete invite
  const canDeleteInvite = (invite: UnifiedProjectInvitation) => {
    if (
      user?.role === Roles.ADMIN ||
      user?.role === Roles.CLIENT ||
      user?._id === project.createdBy._id ||
      user._id === invite.invitedBy._id
    ) {
      return true;
    }
    return false;
  };

  const getRoleBadgeVariant = (role: UserRole | "Owner") => {
    switch (role) {
      case "Owner":
        return "destructive";
      case Roles.ADMIN:
        return "destructive";
      case Roles.VENDOR:
        return "default";
      case Roles.CONTRACTOR:
        return "secondary";
      case Roles.CLIENT:
      default:
        return "outline";
    }
  };

  const inviteMutation = useMutation({
    mutationFn: async (data: InviteMemberValues) => {
      return addMemberToProject(project._id, {
        role:
          checkedUser && checkedUser.exists ? checkedUser.user.role : data.role,
        email: data.email,
      });
    },
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: (_data, variables) => {
      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${variables.email}`,
      });
      form.reset({
        email: "",
        role: Roles.CONTRACTOR,
      });
      setCheckedUser(null);
      setIsSheetOpen(false);
    },
    onError: (err: AxiosError<{ error: string }>) => {
      console.error("Invitation error:", err);
      toast({
        title: "Error",
        description:
          err?.response?.data?.error ||
          "There was a problem sending the invitation.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const onSubmit = (data: InviteMemberValues) => {
    if (!user) return;
    if (!checkedUser) {
      setEmailError("Please check the user before inviting.");
      return;
    }
    inviteMutation.mutate(data);
  };

  // Helper to check if invite is pending (not accepted or declined)
  const isPendingInvite = (invite: UnifiedProjectInvitation) => {
    return !invite.acceptedAt && !invite.declinedAt;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Project Members</h2>
        {canAddMembers && (
          <>
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button size="sm" className="shadow-sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </SheetTrigger>
              <SheetContent className="sm:max-w-[425px]">
                <SheetHeader>
                  <SheetTitle>Add Project Member</SheetTitle>
                  <SheetDescription>
                    Invite a new member to join this project. They will receive
                    an email with instructions to join.
                  </SheetDescription>
                </SheetHeader>

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4 py-4"
                  >
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <div className="relative flex items-center">
                            <FormControl>
                              <Input
                                ref={emailInputRef}
                                placeholder="user@example.com"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setCheckedUser(null);
                                  setEmailError(null);
                                  setIsCheckingUser(false);
                                  // Reset checkedUser and emailError for this email only
                                  const newEmail = e.target.value;
                                  if (checkedUsersMap[newEmail]) {
                                    setCheckedUser(checkedUsersMap[newEmail]);
                                    setEmailError(
                                      checkedUsersMap[newEmail].error || null
                                    );
                                  } else {
                                    setCheckedUser(null);
                                    setEmailError(null);
                                  }
                                }}
                                onKeyDown={async (e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    await handleCheckUser();
                                  }
                                }}
                                autoComplete="off"
                              />
                            </FormControl>
                            <Button
                              type="button"
                              size="sm"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
                              onClick={handleCheckUser}
                              disabled={
                                isCheckingUser || !form.getValues("email")
                              }
                              variant="ghost"
                            >
                              {isCheckingUser ? "Checking..." : "Check"}
                            </Button>
                          </div>
                          <FormDescription>
                            This email will receive the invitation.
                          </FormDescription>

                          {(emailError ||
                            (checkedUser && checkedUser.error)) && (
                            <div className="text-destructive text-xs mt-1">
                              {emailError || checkedUser?.error}
                            </div>
                          )}
                          {checkedUser &&
                            checkedUser.exists &&
                            checkedUser.user &&
                            !emailError && (
                              <div className="text-success text-xs mt-1">
                                User exists:{" "}
                                {`${checkedUser.user.firstName} ${checkedUser.user.lastName}`.trim() ||
                                  checkedUser.user.email}
                              </div>
                            )}
                          {checkedUser &&
                            !checkedUser.exists &&
                            !emailError && (
                              <div className="text-warning text-xs mt-1">
                                No user found. You can invite a new user.
                              </div>
                            )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {checkedUser && checkedUser.exists && checkedUser.user ? (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <div className="px-2 py-2 border rounded bg-muted text-muted-foreground">
                          {checkedUser.user.role.charAt(0).toUpperCase() +
                            checkedUser.user.role.slice(1)}
                        </div>
                        <FormDescription>
                          This user already exists. Their role cannot be
                          changed.
                        </FormDescription>
                      </FormItem>
                    ) : (
                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Role</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={checkedUser && checkedUser.exists}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a role" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {(user?.role === Roles.ADMIN ||
                                  user?.role === Roles.VENDOR) && (
                                  <>
                                    <SelectItem value={Roles.VENDOR}>
                                      Vendor
                                    </SelectItem>
                                  </>
                                )}
                                <SelectItem value={Roles.CLIENT}>
                                  Client
                                </SelectItem>
                                <SelectItem value={Roles.CONTRACTOR}>
                                  Contractor
                                </SelectItem>
                                <SelectItem value={Roles.MEMBER}>
                                  Member
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              This determines what permissions the user will
                              have.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </form>
                </Form>

                <SheetFooter>
                  <Button
                    onClick={form.handleSubmit(onSubmit)}
                    disabled={isLoading || !checkedUser || isCheckingUser}
                  >
                    {isLoading ? "Sending..." : "Send Invitation"}
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </>
        )}
      </div>

      {/* Project Invites Table (only if there are invites) */}
      {isFetchingProjectInvites ? (
        <Card className="shadow-sophisticated border-none">
          <div className="p-6 pb-0 font-semibold text-lg">Pending Invitations</div>
          <div className="p-6">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border/50">
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(1)].map((_, i) => (
                  <TableRow key={i} className="border-b border-border/50">
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        projectInvites &&
        projectInvites.length > 0 && (
          <Card className="shadow-sophisticated border-none">
            <div className="p-6 pb-0 font-semibold text-lg">Pending Invitations</div>
            <div className="p-6">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b border-border/50">
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectInvites.map((invite) => {
                    const pending = isPendingInvite(invite);
                    const expired =
                      !invite.acceptedAt &&
                      !invite.declinedAt &&
                      new Date(invite.expiresAt) < new Date();

                    // Get status and badge variant for invite
                    const getInviteStatus = (
                      invite: UnifiedProjectInvitation
                    ) => {
                      // Only allow variants from Badge: "success" | "destructive" | "outline" | "secondary" | "default" | "warning"
                      if (invite.acceptedAt)
                        return {
                          status: "Accepted",
                          variant: "success" as const,
                        };
                      if (invite.declinedAt)
                        return {
                          status: "Declined",
                          variant: "destructive" as const,
                        };
                      if (expired)
                        return { status: "Expired", variant: "outline" as const };
                      return { status: "Pending", variant: "secondary" as const };
                    };
                    const { status, variant } = getInviteStatus(invite);

                    return (
                      <TableRow key={invite._id} className="border-b border-border/50 hover:bg-muted/30">
                        <TableCell className="font-medium">{invite.email}</TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(invite.role as UserRole)}>
                            {invite.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {typeof invite.invitedBy === "object"
                            ? `${invite.invitedBy.firstName} ${invite.invitedBy.lastName}`.trim() || invite.invitedBy.email
                            : invite.invitedBy}
                        </TableCell>
                        <TableCell>
                          <Badge variant={variant}>{status}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {expired && canDeleteInvite && (
                              <ResendProjectInviteDialog
                                project={project}
                                invitation={invite}
                              />
                            )}
                            {(pending || expired) && canDeleteInvite && (
                              <DeleteProjectInviteDialog
                                project={project}
                                invitation={invite}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      )}

      {/* Members Table */}
      <Card className="shadow-sophisticated border-none">
        <div className="p-6">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border/50">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added By</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {project.createdBy && (
                <TableRow key={project.createdBy._id} className="border-b border-border/50 hover:bg-muted/30">
                  <TableCell className="font-medium">
                    {`${project.createdBy.firstName} ${project.createdBy.lastName}`.trim()}
                  </TableCell>
                  <TableCell>{project.createdBy.email}</TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant("Owner")}>
                      {"Owner"}
                    </Badge>
                  </TableCell>
                  <TableCell>{`${project.createdBy.firstName} ${project.createdBy.lastName}`.trim()}</TableCell>
                  <TableCell>
                    {user.role === Roles.ADMIN &&
                      user._id !== project.createdBy._id && (
                        <RemoveMemberDialog
                          project={project}
                          member={project.createdBy}
                        />
                      )}
                  </TableCell>
                </TableRow>
              )}
              {project?.members?.map((member) => (
                <TableRow key={member._id} className="border-b border-border/50 hover:bg-muted/30">
                  <TableCell className="font-medium">{`${member.firstName} ${member.lastName}`.trim()}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(member.role)}>
                      {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>{`${project.createdBy.firstName} ${project.createdBy.lastName}`.trim()}</TableCell>
                  <TableCell>
                    {canRemoveMember(member._id) && (
                      <RemoveMemberDialog project={project} member={member} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default ProjectMembersTab;
